/**
 * hints.ts — Gallowcrest tutorial hint definitions.
 *
 * 8 progressive hints triggered at key gameplay moments.
 * Each hint appears once per playthrough via HintManager.
 */

import type { HintDefinition, HintTriggerType } from '@ui/HintManager';

// Extend HintTriggerType for Gallowcrest-specific triggers
// The HintManager uses string-based triggers, so we define Gallowcrest's triggers
// as a compatible union. HintManager accepts any string trigger type.

export type GallowcrestHintTrigger =
  | 'first_combat'
  | 'first_skill_check'
  | 'first_travel'
  | 'first_faction'
  | 'first_boss'
  | 'first_achievement'
  | 'first_skill_tree'
  | 'first_class_ability';

export const GALLOWCREST_HINTS: HintDefinition[] = [
  {
    id: 'wh_first_combat',
    title: 'The Art of Battle',
    trigger: 'first_combat' as HintTriggerType,
    color: 'red',
    lines: [
      'Combat in Gallowcrest uses a stance-based system.',
      'Choose your stance each round: Aggressive, Balanced,',
      'Defensive, or Evasive. Each has different modifiers.',
      '',
      'Use class abilities for powerful special attacks.',
      'You can also attempt to Flee if things go badly.',
    ],
  },
  {
    id: 'wh_first_skill_check',
    title: 'Testing Your Mettle',
    trigger: 'first_skill_check' as HintTriggerType,
    color: 'frostSilver',
    lines: [
      'Skill checks are deterministic — no dice rolls!',
      'Your stat value is compared against a threshold.',
      'Higher stats mean better narrative outcomes.',
      '',
      'Skill tree bonuses and equipment also apply.',
      'Build your character to match your playstyle.',
    ],
  },
  {
    id: 'wh_first_travel',
    title: 'The Frozen Road',
    trigger: 'first_travel' as HintTriggerType,
    color: 'frostbiteCyan',
    lines: [
      'The world of the Greyveil has 13 connected locations.',
      'Travel between them using the world map.',
      '',
      'Watch your Frostbite meter — exposure during',
      'travel increases it. Reaching 100% is fatal.',
      'Discover new locations to reveal the full map.',
    ],
  },
  {
    id: 'wh_first_faction',
    title: 'Allegiances',
    trigger: 'first_faction' as HintTriggerType,
    color: 'runeGold',
    lines: [
      'Three factions vie for power in the Greyveil:',
      'the Winterguard, the Resistance, and the',
      'Ancient Powers.',
      '',
      'Your choices affect reputation with each faction.',
      'High standing unlocks titles and unique options.',
    ],
  },
  {
    id: 'wh_first_boss',
    title: 'Worthy Adversary',
    trigger: 'first_boss' as HintTriggerType,
    color: 'corruptionPurple',
    lines: [
      'Boss enemies use advanced 3-phase AI patterns.',
      'They change tactics as the fight progresses.',
      '',
      'Watch their stance shifts for tells. Manage your',
      'HP carefully — bosses hit harder than regular foes.',
      'Victory grants significant rewards and story progress.',
    ],
  },
  {
    id: 'wh_first_achievement',
    title: 'Deeds Remembered',
    trigger: 'first_achievement' as HintTriggerType,
    color: 'runeGold',
    lines: [
      'Achievements track your heroic (and infamous) deeds.',
      '50 achievements across 7 categories await.',
      '',
      'Some have progressive tiers (Bronze/Silver/Gold).',
      'Hidden achievements reveal themselves when unlocked.',
      'Check the Achievements screen for your progress.',
    ],
  },
  {
    id: 'wh_first_skill_tree',
    title: 'Growing Power',
    trigger: 'first_skill_tree' as HintTriggerType,
    color: 'magenta',
    lines: [
      'Each class has 3 skill branches with 5 skills each.',
      'Spend skill points earned from leveling up.',
      '',
      'Higher-tier skills require prerequisites in the',
      'same branch. Skills boost combat and stat checks.',
      'Choose wisely — skill points are limited!',
    ],
  },
  {
    id: 'wh_first_class_ability',
    title: 'Awakened Potential',
    trigger: 'first_class_ability' as HintTriggerType,
    color: 'frostBlue',
    lines: [
      'Class abilities evolve through 4 tiers as you',
      'progress through the story and unlock triggers.',
      '',
      'Each class has 3 unique abilities that grow stronger.',
      'Evolution happens automatically at story milestones.',
      'Higher tiers unlock more powerful combat effects.',
    ],
  },
];
